import { ChevronRight, ArrowUpRight, ArrowDownRight, Minus, Edit3, Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

// FIN-5 — CategoriaTableRow.
// Click na linha → drill down pra Extrato com category pré-aplicado.
// Hover: Edit + Delete revelam.

const fmtBRL = (v) => Number(v ?? 0).toLocaleString("pt-BR", {
  style: "currency", currency: "BRL",
});

function DeltaBadge({ delta }) {
  if (delta == null) {
    return <span className="text-foreground-subtle text-caption">—</span>;
  }
  if (delta === 0) {
    return (
      <Badge variant="outline" className="text-micro inline-flex items-center gap-0.5">
        <Minus className="w-3 h-3" /> 0%
      </Badge>
    );
  }
  const positive = delta > 0;
  // Em despesas: SUBIR é ruim (vermelho), DESCER é bom (verde) — invertido
  const Icon = positive ? ArrowUpRight : ArrowDownRight;
  return (
    <Badge
      variant={positive ? "danger" : "success"}
      className="text-micro inline-flex items-center gap-0.5"
    >
      <Icon className="w-3 h-3" />
      {positive ? "+" : ""}{delta.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%
    </Badge>
  );
}

export default function CategoriaTableRow({
  row,
  onDrillDown,
  onEdit,
  onDelete,
  variant = "table",
}) {
  if (variant === "compact") {
    return (
      <div
        className="group/row relative glass-card rounded-xl p-3.5 hover:border-brand-cyan/30 transition-all cursor-pointer"
        onClick={() => onDrillDown?.(row)}
        data-testid={`cat-row-${row.name.toLowerCase().replace(/\s/g, "-")}`}
      >
        <div className="flex items-center gap-3">
          <span
            className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 text-base"
            style={{ backgroundColor: `${row.color}1A` /* 10% alpha */ }}
            aria-hidden="true"
          >
            {row.icon || "🏷️"}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-foreground font-semibold truncate">{row.name}</p>
            <p className="text-micro text-foreground-muted">
              {row.transactions_count} compra{row.transactions_count === 1 ? "" : "s"} · {row.pct_of_total}%
            </p>
          </div>
          <div className="text-right shrink-0">
            <p className="font-mono font-bold text-foreground tabular-nums">
              {fmtBRL(row.total_current)}
            </p>
            <DeltaBadge delta={row.delta_pct} />
          </div>
          <ChevronRight className="w-4 h-4 text-foreground-muted shrink-0" aria-hidden="true" />
        </div>
        <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover/row:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onEdit?.(row); }}
            className="p-1.5 rounded-md bg-surface-card-hover text-foreground-muted hover:text-foreground"
            aria-label="Editar"
            data-testid={`cat-row-${row.name.toLowerCase().replace(/\s/g, "-")}-edit`}
          >
            <Edit3 className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDelete?.(row); }}
            className="p-1.5 rounded-md bg-status-error/10 text-status-error/80 hover:bg-status-error/20"
            aria-label="Apagar"
            data-testid={`cat-row-${row.name.toLowerCase().replace(/\s/g, "-")}-delete`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  }

  // table row
  return (
    <tr
      className="group/row border-b border-subtle/50 transition-all duration-fast hover:bg-surface-card-hover cursor-pointer"
      onClick={() => onDrillDown?.(row)}
      data-testid={`cat-row-${row.name.toLowerCase().replace(/\s/g, "-")}`}
    >
      <td className="py-3 pr-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <span
            className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-base"
            style={{ backgroundColor: `${row.color}1A` }}
            aria-hidden="true"
          >
            {row.icon || "🏷️"}
          </span>
          <span className="text-foreground font-semibold truncate">{row.name}</span>
        </div>
      </td>
      <td className="py-3 px-3 font-mono font-bold text-foreground tabular-nums whitespace-nowrap">
        {fmtBRL(row.total_current)}
      </td>
      <td className="py-3 px-3 whitespace-nowrap">
        <DeltaBadge delta={row.delta_pct} />
      </td>
      <td className="py-3 px-3 text-foreground-muted text-caption tabular-nums whitespace-nowrap">
        {row.pct_of_total.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%
      </td>
      <td className="py-3 px-3 text-foreground-muted text-caption tabular-nums whitespace-nowrap">
        {row.transactions_count}
      </td>
      <td className="py-3 pl-3 text-right relative whitespace-nowrap">
        <ChevronRight className="w-4 h-4 text-foreground-muted inline-block" aria-hidden="true" />
        <div className="absolute top-1/2 -translate-y-1/2 right-1 flex items-center gap-1 opacity-0 group-hover/row:opacity-100 transition-opacity bg-surface-overlay/95 backdrop-blur px-1 rounded-md">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onEdit?.(row); }}
            className="p-1.5 rounded-md text-foreground-muted hover:bg-surface-card-hover hover:text-foreground"
            aria-label="Editar categoria"
            data-testid={`cat-row-${row.name.toLowerCase().replace(/\s/g, "-")}-edit`}
          >
            <Edit3 className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDelete?.(row); }}
            className="p-1.5 rounded-md text-status-error/80 hover:bg-status-error/10 hover:text-status-error"
            aria-label="Apagar categoria"
            data-testid={`cat-row-${row.name.toLowerCase().replace(/\s/g, "-")}-delete`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
}
